import getConfig from 'next/config';
import { CouponClient, LanguageBasedVariant, Product } from 'types';
import { nanoid } from 'nanoid';
import {
  ClientSubscription,
  ClientSubscriptionProduct,
} from 'types/Subscription.types';

export const buildQuantityOptions = (
  quantityLimits: Array<number | null> | undefined
) => {
  const quantityLimitOptions: { label: string; value: string }[] = [];
  if (quantityLimits) {
    quantityLimits.forEach(quantity => {
      if (quantity !== null) {
        quantityLimitOptions.push({
          label: `${quantity}`,
          value: `${quantity}`,
        });
      }
    });
  }
  return quantityLimitOptions;
};

export const buildLanguageVariants = (variants: LanguageBasedVariant[]) => {
  const languageVariantsOptions: { label: string; value: string }[] = [];
  if (variants) {
    variants.forEach(variant => {
      if (variant !== null) {
        languageVariantsOptions.push({
          label: `${variant.language}`,
          value: `${variant.variantId}`,
        });
      }
    });
  }
  return languageVariantsOptions;
};

export const isProductInStock = (backorderable: boolean, in_stock: boolean) => {
  return backorderable || in_stock;
};

export const isLineItemInactive = (quantityLimit: number[]) => {
  return quantityLimit.length === 1 && quantityLimit[0] === 0;
};

export const isLineItemPurchasable = (
  backorderable: boolean,
  in_stock: boolean,
  quantityLimit: number[]
) => {
  return (
    isProductInStock(backorderable, in_stock) &&
    !isLineItemInactive(quantityLimit)
  );
};

export const buildEmptyProduct = () => {
  const productPreview: Product = {
    id: nanoid(),
    variantId: '',
    slug: '',
    name: '',
    sku: '',
    price: 0,
    displayPrice: '',
    description: '',
    isHavingBulkDiscount: false,
    images: [
      {
        id: nanoid(),
        thumbnail:
          'data:image/svg+xml;base64,Cjxzdmcgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPSIjY2JjYmNiIiBvZmZzZXQ9IjIwJSIgLz4KICAgICAgPHN0b3Agc3RvcC1jb2xvcj0iI2Y5ZjlmOSIgb2Zmc2V0PSI1MCUiIC8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9IiNjYmNiY2IiIG9mZnNldD0iNzAlIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIGZpbGw9IiNjYmNiY2IiIC8+CiAgPHJlY3QgaWQ9InIiIHdpZHRoPSI3MDAiIGhlaWdodD0iNDc1IiBmaWxsPSJ1cmwoI2cpIiAvPgogIDxhbmltYXRlIHhsaW5rOmhyZWY9IiNyIiBhdHRyaWJ1dGVOYW1lPSJ4IiBmcm9tPSItNzAwIiB0bz0iNzAwIiBkdXI9IjFzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSIgIC8+Cjwvc3ZnPg==',
        original:
          'data:image/svg+xml;base64,Cjxzdmcgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPSIjY2JjYmNiIiBvZmZzZXQ9IjIwJSIgLz4KICAgICAgPHN0b3Agc3RvcC1jb2xvcj0iI2Y5ZjlmOSIgb2Zmc2V0PSI1MCUiIC8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9IiNjYmNiY2IiIG9mZnNldD0iNzAlIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIGZpbGw9IiNjYmNiY2IiIC8+CiAgPHJlY3QgaWQ9InIiIHdpZHRoPSI3MDAiIGhlaWdodD0iNDc1IiBmaWxsPSJ1cmwoI2cpIiAvPgogIDxhbmltYXRlIHhsaW5rOmhyZWY9IiNyIiBhdHRyaWJ1dGVOYW1lPSJ4IiBmcm9tPSItNzAwIiB0bz0iNzAwIiBkdXI9IjFzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSIgIC8+Cjwvc3ZnPg==',
      },
    ],
    inStock: false,
    quantityLimit: [],
    bulkDiscount: {
      title: '',
      description: '',
    },
    breadcrumb: [],
    languageBasedVariants: [],
    assets: [],
    materialType: '',
    isCustomizable: false,
    dimensions: '',
    discontinueOn: '',
    AvailableOn: '',
  };

  return productPreview;
};

export const isCustomizableClamshellProduct = (sku: string) => {
  const { publicRuntimeConfig } = getConfig();

  const customizableProducts =
    publicRuntimeConfig.CUSTOMIZABLE_PRODUCT_SKU.split(',');

  const isCustomizable =
    customizableProducts.length > 0 && customizableProducts.includes(sku);
  return isCustomizable;
};

export const COUPON_OFFER = {
  ribbonText: 'Coupon',
};

// formatting coupon data to match the format what coupon details component using
export const formatCouponDetails = (couponData?: CouponClient) => [
  {
    promoCode: couponData?.code ?? '',
    promoTotal: '',
  },
];

export const isIteroScannerProduct = (sku: string) => {
  const { publicRuntimeConfig } = getConfig();

  const customizableProducts = publicRuntimeConfig.SCANNER_SKUS.split(',');

  const isScannerProduct =
    customizableProducts.length > 0 && customizableProducts.includes(sku);
  return isScannerProduct;
};

export const buildSubscriptionFrequenciesByQuantity = (
  product: ClientSubscriptionProduct | undefined,
  selectedQuantity: number
) => {
  if (!product || !product.options || !selectedQuantity) return [];

  const subscriptionFrequencyOptions: { label: string; value: string }[] = [];

  const availableFrequenciesForQuantity = product.options.find(
    option => option.quantity === selectedQuantity
  )?.frequencies;

  availableFrequenciesForQuantity?.forEach(freq => {
    subscriptionFrequencyOptions.push({
      label: `${freq.displayText} (${freq.discount} off)`,
      value: freq.id,
    });
  });

  return subscriptionFrequencyOptions;
};

export const getAvailableSubscriptionQuantities = (
  product: ClientSubscriptionProduct | undefined
) => {
  if (!product || !product.options) return [];

  return product.options.map(option => ({
    label: option.quantity.toString(),
    value: option.quantity.toString(),
  }));
};

export const getOriginalPriceWithoutSubscription = (
  subscriptionProduct: ClientSubscriptionProduct,
  selectedSubscriptionFrequencyId: string,
  selectedSubscriptionQuantity: number
) => {
  return subscriptionProduct?.options
    .find(option => option.quantity === selectedSubscriptionQuantity)
    ?.frequencies.find(
      frequency => frequency.id === selectedSubscriptionFrequencyId
    )?.originalPrice;
};

export const getDiscountedPriceWithSubscription = (
  subscriptionProduct: ClientSubscriptionProduct,
  selectedSubscriptionFrequencyId: string,
  selectedSubscriptionQuantity: number
) => {
  return subscriptionProduct?.options
    .find(option => option.quantity === selectedSubscriptionQuantity)
    ?.frequencies.find(
      frequency => frequency.id === selectedSubscriptionFrequencyId
    )?.discountedPrice;
};

export const getDiscountedPercentWithSubscription = (
  subscriptionProduct: ClientSubscriptionProduct | undefined,
  selectedSubscriptionFrequencyId: string,
  selectedSubscriptionQuantity: number
) => {
  return subscriptionProduct?.options
    .find(option => option.quantity === selectedSubscriptionQuantity)
    ?.frequencies.find(
      frequency => frequency.id === selectedSubscriptionFrequencyId
    )?.discount;
};

export const filterActiveSubscriptions = (
  flattenedSubscriptionHistory: ClientSubscription[],
  productSku: string
) => {
  if (
    !flattenedSubscriptionHistory ||
    flattenedSubscriptionHistory.length === 0
  ) {
    return [];
  }

  return flattenedSubscriptionHistory.filter(
    subscription =>
      subscription.variantSku === productSku && subscription.status === 'active'
  );
};

export const translateFrequencyLabel = (
  frequency: { label: string; value: string },
  i18n: Record<string, string>
) => {
  const frequencymatch = frequency.label.match(/^(.*?)\s*(\(.*\))$/);

  if (!frequencymatch) {
    return frequency;
  }

  const frequencyText = frequencymatch[1];
  let suffixText = frequencymatch[2];

  suffixText = suffixText.replace(/\boff\b/i, i18n['off']);

  return {
    ...frequency,
    label: `${i18n[frequencyText] ?? frequencyText} ${suffixText}`,
  };
};

export const buildLimitExceededErrorMessage = (
  error: any,
  exceededItemsMessage: string
): string |  {
  name: string;
  max_quantity: number;
  requested_quantity: number;
}[]
| undefined => {
  if (!error) return;

  if (typeof error === 'string') {
    return error;
  
  }

  if (Array.isArray(error)) {
    return error
      .map(
        (item: any) =>
          `${exceededItemsMessage} ${item.max_quantity} ${item.name} (${item.sku}).`
      )
      .join('\n');
  }};

